import { useState } from 'react';
import { c, fmtShort, tone, tracked } from '../theme';
import type { OfferWindow } from '../api';
import { confidenceText, methodStanding, priceForRating } from '../../worker/offer.ts';

/**
 * The offer window, set out so a buyer can check it: the methods with
 * their figures, confidence and reasons, the indicative price as an input with
 * its stated weight, why the window is the width it is, and where to open,
 * aim and walk away. Not a valuation, and it says so.
 */

const CONF: Record<'High' | 'Medium' | 'Low', string> = { High: tone.strong, Medium: tone.ok, Low: tone.warn };

const AMBER = '#C9741A';
const GREEN = '#1E8A5A';
const HAIR = 'rgba(31,26,31,0.12)';
const MUTED = 'rgba(31,26,31,0.6)';
const METHOD_TONE: Record<string, string> = { M1: c.red, M2: c.cyan, M3: c.magenta, M4: c.violet };
const rgba = (hex: string, a: number) =>
  `rgba(${parseInt(hex.slice(1, 3), 16)},${parseInt(hex.slice(3, 5), 16)},${parseInt(hex.slice(5, 7), 16)},${a})`;

/** "$2.35" + "M", "$575" + "K" — the strip's figure and its small suffix. */
function figure(v: number): [string, string] {
  if (v >= 1e6) return [`$${(Math.round(v / 1e4) / 100).toFixed(2)}`, 'M'];
  return [`$${Math.round(v / 1e3)}`, 'K'];
}

/**
 * The window on one dollar scale, after the Offer Window handoff: the band,
 * the midpoint line, each method as a tone indicator on a dotted stem (M1 red, M4 violet,
 * M2 cyan, M3 magenta), labels staggered so close values never collide, and
 * the advertised price as a red diamond on the baseline.
 */
function OfferChart({ offer, agentPrice }: { offer: OfferWindow; agentPrice?: number | null }) {
  const live = offer.methods.filter((m) => m.mid != null) as (OfferWindow['methods'][number] & { mid: number })[];
  const ask = offer.indicative?.price ?? null;
  const agent = agentPrice ?? null;
  const pts = [offer.low, offer.high, ...live.map((m) => m.mid), ...(ask ? [ask] : []), ...(agent ? [agent] : [])];
  const span = Math.max(...pts) - Math.min(...pts) || offer.mid * 0.1;
  const a = Math.min(...pts) - span * 0.08;
  const b = Math.max(...pts) + span * 0.08;
  const pct = (v: number) => ((v - a) / (b - a)) * 100;
  const step = [25e3, 50e3, 100e3, 250e3, 500e3, 1e6, 2e6].find((s) => (b - a) / s <= 4.5) ?? 5e6;
  const ticks: number[] = [];
  for (let t = Math.ceil(a / step) * step; t <= b; t += step) ticks.push(t);
  const tickLabel = (t: number) => (t >= 1e6 ? `$${+(t / 1e6).toFixed(2)}m` : `$${Math.round(t / 1e3)}k`);

  // Stagger: each label takes the highest lane clear of the labels already placed.
  const LANES = [24, 52, 80];
  const placed: { lane: number; x: number }[] = [];
  const labels = [...live]
    .sort((p, q) => p.mid - q.mid)
    .map((m) => {
      const x = pct(m.mid);
      const lane = LANES.find((l) => placed.every((p) => p.lane !== l || Math.abs(p.x - x) > 26)) ?? LANES[LANES.length - 1];
      placed.push({ lane, x });
      return { m, x, lane };
    });
  const BASE = 122;

  return (
    <div style={{ position: 'relative', height: 178 }} role="img" aria-label={`Offer window ${fmtShort(offer.low)} to ${fmtShort(offer.high)}, midpoint ${fmtShort(offer.mid)}; ${live.map((m) => `${m.name} ${fmtShort(m.mid)}`).join('; ')}${ask ? `; advertised ${fmtShort(ask)}` : ''}${agent ? `; agent's likely sale price ${fmtShort(agent)}` : ''}`}>
      <div style={{ position: 'absolute', left: `${pct(offer.mid)}%`, top: 0, transform: 'translateX(-50%)', fontSize: 9, letterSpacing: '0.16em', fontWeight: 700, color: c.violet }}>MIDPOINT</div>
      {labels.map(({ m, x, lane }) => {
        const flip = x > 72;
        return (
          <div key={m.key}>
            <div
              title={m.name}
              style={{
                position: 'absolute',
                zIndex: 2,
                left: `${x}%`,
                top: lane,
                display: 'flex',
                flexDirection: flip ? 'row-reverse' : 'row',
                alignItems: 'center',
                gap: 6,
                transform: flip ? 'translateX(calc(-100% + 11px))' : 'translateX(-11px)',
              }}
            >
              <ToneDotSmall tone={METHOD_TONE[m.key]} />
              <span style={{ fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', background: c.surface, padding: '2px 3px', borderRadius: 4 }}>
                {m.key} <span style={{ fontWeight: 600 }}>{fmtShort(m.mid)}</span>
              </span>
            </div>
            <div
              style={{
                position: 'absolute',
                left: `${x}%`,
                top: lane + 20,
                height: BASE - 8 - (lane + 20),
                width: 2,
                transform: 'translateX(-1px)',
                borderRadius: 1,
                background: `linear-gradient(180deg, ${rgba(METHOD_TONE[m.key], 0.75)}, ${rgba(METHOD_TONE[m.key], 0.08)})`,
              }}
            />
          </div>
        );
      })}
      {/* A raised rail rather than a hairline */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: BASE - 1, height: 3, borderRadius: 2, background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(31,26,31,0.14))', boxShadow: '0 1px 2px rgba(31,26,31,0.18)' }} />
      <div
        style={{
          position: 'absolute',
          left: `${pct(offer.low)}%`,
          width: `${pct(offer.high) - pct(offer.low)}%`,
          top: BASE - 11,
          height: 20,
          borderRadius: 999,
          // Extruded like a ring segment: a lit violet face over four darker
          // edge layers, a soft drop shadow, and a gloss across the top.
          background: 'linear-gradient(180deg, #A783F2 0%, #7C45E3 50%, #6A2BD9 100%)',
          boxShadow:
            '0 1px 0 #5A24BC, 0 2px 0 #5221AD, 0 3px 0 #4A1E9E, 0 4px 0 #431B8F, 0 10px 16px rgba(67,27,143,0.35)',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'absolute', left: 6, right: 6, top: 2, height: 7, borderRadius: 999, background: 'linear-gradient(180deg, rgba(255,255,255,0.65), rgba(255,255,255,0))' }} />
      </div>
      {/* The midpoint as a lit pillar */}
      <div
        style={{
          position: 'absolute',
          left: `${pct(offer.mid)}%`,
          top: 16,
          bottom: 40,
          width: 6,
          transform: 'translateX(-3px)',
          borderRadius: 3,
          zIndex: 1,
          background: 'linear-gradient(90deg, #4A1E9E 0%, #A783F2 42%, #6A2BD9 70%, #4A1E9E 100%)',
          boxShadow: '0 0 12px rgba(106,43,217,0.45), 0 3px 4px rgba(67,27,143,0.35)',
        }}
      />
      {agent != null && (
        <div
          title={`Agent's likely sale price ${fmtShort(agent)}`}
          style={{
            position: 'absolute',
            left: `${pct(agent)}%`,
            top: BASE - 9,
            width: 17,
            height: 17,
            transform: 'translateX(-8.5px)',
            borderRadius: '50%',
            background: c.surface,
            border: `4px solid ${AMBER}`,
            boxSizing: 'border-box',
            zIndex: 2,
            boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.75), inset 0 -1px 2px rgba(110,55,5,0.45), 0 1px 0 #9A5512, 0 2px 0 #8A4B0F, 0 5px 8px rgba(110,55,5,0.35)',
          }}
        />
      )}
      {ask != null && (
        <div
          title={`Advertised ${fmtShort(ask)}`}
          style={{
            position: 'absolute',
            left: `${pct(ask)}%`,
            top: BASE - 8,
            width: 14,
            height: 14,
            zIndex: 2,
            transform: 'translateX(-7px) rotate(45deg)',
            borderRadius: 2,
            // A cut gem: light from the top-left facet, deep red to the bottom-right.
            background: 'linear-gradient(135deg, #FF9D90 0%, #E5503F 38%, #D63A2E 55%, #8E1F16 100%)',
            boxShadow: '1px 1px 0 #7A1A12, 2px 2px 0 #6A160F, 3px 4px 6px rgba(110,24,16,0.4), inset 1px 1px 1px rgba(255,255,255,0.7)',
          }}
        />
      )}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 146, height: 24 }}>
        {ticks.map((t) => (
          <div key={t}>
            <div style={{ position: 'absolute', left: `${pct(t)}%`, top: 0, height: 6, borderLeft: '1px solid rgba(31,26,31,0.3)' }} />
            <div style={{ position: 'absolute', left: `${pct(t)}%`, top: 10, transform: 'translateX(-50%)', fontSize: 10, fontWeight: 600, color: MUTED, whiteSpace: 'nowrap' }}>{tickLabel(t)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** A method marker as a glossy sphere in its tone, sitting in a soft halo. */
function ToneDotSmall({ tone }: { tone: string }) {
  return (
    <span style={{ width: 22, height: 22, flex: 'none', borderRadius: '50%', background: rgba(tone, 0.16), boxShadow: `0 0 10px ${rgba(tone, 0.28)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <span
        style={{
          width: 13,
          height: 13,
          borderRadius: '50%',
          background: `radial-gradient(circle at 35% 30%, #FFFFFF 0%, ${rgba(tone, 0.95)} 38%, ${tone} 60%, ${rgba(tone, 1)} 100%)`,
          boxShadow: `inset -2px -2px 3px rgba(0,0,0,0.25), 0 2px 3px ${rgba(tone, 0.45)}`,
        }}
      />
    </span>
  );
}

/**
 * The top of the offer window, built to the Offer Window handoff: intro,
 * range strip, confidence pill, chart and legend. The overview shows it with
 * its button; the tab shows it above the methods.
 */
function OfferSummaryBody({ offer, agentPrice }: { offer: OfferWindow; agentPrice?: number | null }) {
  const tone = offer.confidence === 'High' ? GREEN : offer.confidence === 'Medium' ? AMBER : c.red;
  const cells: [string, number, boolean][] = [
    ['LOW', offer.low, false],
    ['MIDPOINT', offer.mid, true],
    ['HIGH', offer.high, false],
  ];
  return (
    <>
      <div style={{ padding: '40px var(--gx) 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
        <div style={{ fontSize: 10, letterSpacing: '0.2em', fontWeight: 600, color: c.red }}>THE OFFER WINDOW</div>
        <div style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.03em' }}>
          Not a valuation<span style={{ color: c.red }}>.</span>
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.45, fontWeight: 500, color: c.ink, maxWidth: 280 }}>The range to negotiate within.</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', marginTop: 34, borderTop: `1px solid ${HAIR}`, borderBottom: `1px solid ${HAIR}` }}>
        {cells.map(([k, v, mid], i) => {
          const [n, unit] = figure(v);
          return (
            <div
              key={k}
              style={{
                padding: '26px 8px 24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                borderRight: i < 2 ? `1px solid ${HAIR}` : 'none',
                background: mid ? 'rgba(106,43,217,0.07)' : 'transparent',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {mid && <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.violet, animation: 'throb 2.4s ease-in-out infinite' }} />}
                <span style={{ fontSize: 9, letterSpacing: '0.16em', fontWeight: 600, color: mid ? c.violet : MUTED }}>{k}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 2, color: mid ? c.violet : c.ink }}>
                <span style={{ fontSize: 24, fontWeight: mid ? 700 : 600, letterSpacing: '-0.02em', lineHeight: 1 }}>{n}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: mid ? c.violet : MUTED }}>{unit}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 18 }}>
        <div style={{ fontSize: 9, letterSpacing: '0.14em', fontWeight: 600, color: tone, border: `1px solid ${rgba(tone, 0.45)}`, padding: '6px 12px', borderRadius: 999 }}>
          {`${confidenceText(offer.confidence).toUpperCase()} · ±${Math.round(offer.halfWidth * 100)}% EITHER SIDE`}
        </div>
      </div>

      <div style={{ padding: '34px 30px 0' }}>
        <OfferChart offer={offer} agentPrice={agentPrice} />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '10px 18px', padding: '18px 30px 0' }}>
        {[
          ['Offer window', <span key="w" style={{ width: 18, height: 9, borderRadius: 999, background: 'linear-gradient(180deg, #A783F2, #6A2BD9)', boxShadow: '0 1px 0 #5221AD, 0 2px 0 #431B8F' }} />],
          ['Midpoint', <span key="m" style={{ width: 4, height: 13, borderRadius: 2, background: 'linear-gradient(90deg, #4A1E9E, #A783F2 45%, #4A1E9E)' }} />],
          ['Method figures', <span key="f" style={{ width: 10, height: 10, borderRadius: '50%', background: 'radial-gradient(circle at 35% 30%, #FFFFFF 0%, #8a8590 40%, #3d363d 100%)' }} />],
          ...(offer.indicative ? [[`${offer.indicative.source === 'buyer' ? 'Your price' : 'Advertised'} ${fmtShort(offer.indicative.price)}`, <span key="a" style={{ width: 8, height: 8, transform: 'rotate(45deg)', borderRadius: 1, background: 'linear-gradient(135deg, #FF9D90, #D63A2E 55%, #8E1F16)', boxShadow: '1px 1px 0 #7A1A12' }} />] as [string, JSX.Element]] : []),
          ...(agentPrice ? [[`Agent's likely price ${fmtShort(agentPrice)}`, <span key="g" style={{ width: 11, height: 11, borderRadius: '50%', border: `3px solid ${AMBER}`, boxSizing: 'border-box', boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.7), 0 1px 0 #8A4B0F' }} />] as [string, JSX.Element]] : []),
        ].map(([label, mark]) => (
          <div key={label as string} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 10, fontWeight: 600, color: 'rgba(31,26,31,0.7)' }}>
            {mark}
            {label}
          </div>
        ))}
      </div>
    </>
  );
}

/** The overview's offer window, with the way into the full tab beneath it. */
export function OfferSummary({ offer, onOpen, agentPrice }: { offer: OfferWindow; onOpen?: () => void; agentPrice?: number | null }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <OfferSummaryBody offer={offer} agentPrice={agentPrice} />
      {onOpen ? (
        <div style={{ padding: '40px var(--gx) 44px' }}>
          <button
            type="button"
            className="rb-cta-violet"
            onClick={onOpen}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              width: '100%',
              boxSizing: 'border-box',
              height: 64,
              padding: '0 20px',
              borderRadius: 999,
              background: c.violet,
              color: c.cream,
              fontSize: 11,
              lineHeight: 1,
              letterSpacing: '0.12em',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            <span>OPEN THE READYBUY OFFER WINDOW</span>
            <svg style={{ flex: 'none' }} width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
              <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      ) : (
        <div style={{ height: 34 }} />
      )}
    </div>
  );
}

/** "$2.2m", "$950k" — a typed offer, read back in the verdict line. */
const shortM = (v: number) => (v >= 1e6 ? `$${+(v / 1e6).toFixed(2)}m` : `$${Math.round(v / 1e3)}k`);

/** Where a typed offer sits against the window — the chip and the line under the bar. */
function placement(v: number, offer: OfferWindow): { tag: string; bg: string; verdict: string } {
  const p = shortM(v);
  if (v < offer.low) return { tag: 'BELOW WINDOW', bg: c.cyan, verdict: `${p} sits under the low end. Expect it to be knocked back.` };
  if (v <= offer.mid) return { tag: 'STRONG', bg: GREEN, verdict: `${p} sits between the low end and the midpoint.` };
  if (v <= offer.high) return { tag: 'FAIR', bg: AMBER, verdict: `${p} sits between the midpoint and the walk-away.` };
  return { tag: 'ABOVE EVIDENCE', bg: c.red, verdict: `${p} is past the walk-away figure.` };
}

const WHY_TONES = [GREEN, AMBER, c.violet];

/**
 * The Offer Window tab: the window itself, the methods behind it, then
 * the Rate an Offer screen from its handoff — test a number against the
 * window, and why the window is this wide.
 */
export function OfferPanel({ offer }: { offer: OfferWindow }) {
  const [typed, setTyped] = useState('');
  const ind = offer.indicative;
  const basis = offer.rate ?? null;
  // "$2.2m", "2.2", "950k", "2200000" all read as dollars.
  const offerValue = (() => {
    const t = typed.toLowerCase().replace(/[$,\s]/g, '');
    const n = parseFloat(t);
    if (!Number.isFinite(n) || n <= 0) return null;
    if (t.endsWith('k')) return Math.round(n * 1_000);
    if (t.endsWith('m') || n < 100) return Math.round(n * 1_000_000);
    if (n < 100_000) return Math.round(n * 1_000);
    return Math.round(n);
  })();
  const placed = offerValue != null ? placement(offerValue, offer) : null;
  const span = offer.high - offer.low;
  const lo = offer.low - span / 2;
  const hi = offer.high + span / 2;
  const at = (v: number) => Math.max(0, Math.min(100, ((v - lo) / (hi - lo)) * 100));
  const eyebrow = { fontSize: 10, letterSpacing: '0.2em', fontWeight: 600, color: c.red } as const;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <OfferSummaryBody offer={offer} />

      {/* The methods, then the indicative price as an input */}
      <div style={{ display: 'flex', flexDirection: 'column', padding: '34px var(--gx) 0' }}>
        <div style={{ ...eyebrow, paddingBottom: 10 }}>THE {['NO', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE'][offer.methods.length] ?? offer.methods.length} METHODS</div>
        {offer.methods.map((m) => (
          <div key={m.key} style={{ display: 'grid', gridTemplateColumns: '22px 1fr auto', gap: '4px 14px', padding: '16px 0', borderTop: `1px solid ${HAIR}` }}>
            <ToneDotSmall tone={METHOD_TONE[m.key] ?? c.ink} />
            <span style={{ fontSize: 13, fontWeight: 600, lineHeight: '22px' }}>{m.name}</span>
            <span style={{ fontSize: 13, fontWeight: 700, lineHeight: '22px', fontVariantNumeric: 'tabular-nums' }}>
              {m.mid != null ? fmtShort(m.mid) : '—'}
            </span>
            <span />
            <span style={{ gridColumn: '2 / 4', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 8, fontWeight: 700, color: c.field, background: m.mid != null ? CONF[m.confidence] : c.ink45, padding: '3px 8px', borderRadius: 999, ...tracked(0.12) }}>
                {m.mid != null ? methodStanding(m.confidence).toUpperCase() : m.setAside ? 'SET ASIDE' : 'NOT AVAILABLE'}
              </span>
              {m.mid == null && <span style={{ fontSize: 10, color: MUTED }}>not used</span>}
            </span>
            <span />
            <span style={{ gridColumn: '2 / 4', fontSize: 12, color: c.ink6, lineHeight: 1.5 }}>{m.why}</span>
          </div>
        ))}
        {ind && (
          <div style={{ display: 'grid', gridTemplateColumns: '22px 1fr auto', gap: '4px 14px', padding: '16px 0', borderTop: `1px solid ${HAIR}`, borderBottom: `1px solid ${HAIR}` }}>
            <span style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }} aria-hidden>
              <span style={{ width: 9, height: 9, background: c.red, transform: 'rotate(45deg)' }} />
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, lineHeight: '22px' }}>{ind.source === 'buyer' ? 'The price you gave us' : 'The advertised price'}</span>
            <span style={{ fontSize: 13, fontWeight: 700, lineHeight: '22px', fontVariantNumeric: 'tabular-nums' }}>{fmtShort(ind.price)}</span>
            <span />
            <span />
            <span style={{ gridColumn: '2 / 4', fontSize: 12, color: c.ink6, lineHeight: 1.5 }}>
              {offer.stale
                ? `It counts for less than usual, because the market has not paid it in ${offer.stale.days} days.`
                : ind.source === 'buyer'
                  ? 'The figure you gave us at the start. A buyer’s own figure counts for less than an agent’s guide.'
                  : 'Agents’ guides have tracked final sale prices closely, so it is a major input to the window.'}
              {ind.note ? ` ${ind.note}` : ''}
            </span>
          </div>
        )}
      </div>

      {/* Rate an offer */}
      <div style={{ padding: '44px var(--gx) 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
        <div style={eyebrow}>RATE AN OFFER</div>
        <div style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.1, letterSpacing: '-0.03em' }}>
          Test your number<span style={{ color: c.red }}>.</span>
        </div>
      </div>
      <div style={{ padding: '28px var(--gx) 0', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 14, height: 64, padding: '0 8px 0 7px', borderRadius: 999, background: c.field, border: `1px solid ${HAIR}`, boxSizing: 'border-box' }}
        >
          <span aria-hidden style={{ width: 50, height: 50, flex: 'none', borderRadius: '50%', background: 'rgba(106,43,217,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.violet, animation: 'throb 2.4s ease-in-out infinite' }} />
          </span>
          <input
            className="rb-pill-input"
            inputMode="decimal"
            aria-label="Your offer"
            placeholder={`e.g. ${shortM(basis ? priceForRating(70, basis) : offer.low)}`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            style={{ flex: 1, minWidth: 0, padding: 0, border: 0, background: 'transparent', fontSize: 17, fontWeight: 500, color: c.ink, outline: 'none' }}
          />
          <span
            aria-live="polite"
            style={{
              flex: 'none',
              padding: '12px 14px',
              borderRadius: 999,
              background: placed ? placed.bg : 'rgba(31,26,31,0.06)',
              color: placed ? c.cream : MUTED,
              fontSize: 9,
              letterSpacing: '0.14em',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {placed ? placed.tag : 'TYPE A PRICE'}
          </span>
        </label>
        {placed && offerValue != null && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 4px 0' }}>
            <div style={{ position: 'relative', height: 20 }}>
              <div style={{ position: 'absolute', left: 0, right: 0, top: 8, height: 3, borderRadius: 2, background: 'linear-gradient(180deg, rgba(255,255,255,0.9), rgba(31,26,31,0.14))', boxShadow: '0 1px 2px rgba(31,26,31,0.18)' }} />
              <div
                style={{
                  position: 'absolute',
                  left: `${at(offer.low)}%`,
                  width: `${at(offer.high) - at(offer.low)}%`,
                  top: 2,
                  height: 14,
                  borderRadius: 999,
                  background: 'linear-gradient(180deg, #A783F2 0%, #7C45E3 50%, #6A2BD9 100%)',
                  boxShadow: '0 1px 0 #5A24BC, 0 2px 0 #4A1E9E, 0 3px 0 #431B8F, 0 8px 12px rgba(67,27,143,0.3)',
                  overflow: 'hidden',
                }}
              >
                <div style={{ position: 'absolute', left: 5, right: 5, top: 1.5, height: 5, borderRadius: 999, background: 'linear-gradient(180deg, rgba(255,255,255,0.65), rgba(255,255,255,0))' }} />
              </div>
              <div style={{ position: 'absolute', left: `${at(offer.mid)}%`, top: -3, bottom: -3, width: 5, transform: 'translateX(-2.5px)', borderRadius: 3, background: 'linear-gradient(90deg, #4A1E9E 0%, #A783F2 42%, #6A2BD9 70%, #4A1E9E 100%)', boxShadow: '0 0 10px rgba(106,43,217,0.45)' }} />
              <div
                style={{
                  position: 'absolute',
                  left: `${at(offerValue)}%`,
                  top: 0,
                  width: 19,
                  height: 19,
                  transform: 'translateX(-9.5px)',
                  borderRadius: '50%',
                  // A glossy sphere in the rating's colour, ringed in white.
                  background: `radial-gradient(circle at 35% 30%, #FFFFFF 0%, ${placed.bg} 45%, ${placed.bg} 100%)`,
                  border: '2px solid #FFFFFF',
                  boxSizing: 'border-box',
                  boxShadow: '0 3px 6px rgba(31,26,31,0.3), inset -2px -2px 3px rgba(0,0,0,0.22)',
                  zIndex: 1,
                }}
              />
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.5, color: c.ink, textAlign: 'center' }}>
              {placed.verdict}
            </div>
          </div>
        )}
      </div>

      {/* Why this window */}
      <div style={{ padding: '40px var(--gx) 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ ...eyebrow, paddingBottom: 10 }}>WHY THIS WINDOW</div>
        <div>
          {offer.why.map((t, i) => (
            <div
              key={t}
              style={{ display: 'grid', gridTemplateColumns: '22px 1fr', gap: 14, alignItems: 'start', padding: '16px 0', borderTop: `1px solid ${HAIR}`, borderBottom: i === offer.why.length - 1 ? `1px solid ${HAIR}` : 'none' }}
            >
              <ToneDotSmall tone={WHY_TONES[i % WHY_TONES.length]} />
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>{t}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ height: 44 }} />
    </div>
  );
}
